import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type { IRoleCompact } from "./Roles";

/**
 * Enum representing the status of an employee profile.
 * @enum {number|null}
 */
export enum EmployeeProfileStatus {
  // biome-ignore lint/style/useLiteralEnumMembers: <explanation>
  All = null,
  Active = 0,
  Inactive = 1,
}

/**
 * Interface representing the parameters for listing employees.
 * @interface IListParams
 * @property {number} [page] - The page number for pagination.
 * @property {number} [page_size] - The number of employees per page.
 * @property {string} [search] - The search term for filtering employees.
 * @property {boolean} [tracked_shifts] - Indicates if tracked shifts should be included.
 * @property {boolean} [has_open_shift] - Indicates if employees with open shifts should be included.
 * @property {EmployeeProfileStatus} [status] - The status of the employee profile to filter by.
 * @property {boolean} [isVenueSpecific] - Indicates if the query is venue-specific.
 */
export interface IListParams {
  page?: number;
  page_size?: number;
  search?: string;
  tracked_shifts?: boolean;
  has_open_shift?: boolean;
  status?: EmployeeProfileStatus;
  isVenueSpecific?: boolean;
}

/**
 * Base interface for pay rate information.
 * @interface IPayRateBase
 * @property {ID} id - The unique identifier for the pay rate.
 * @property {string} label - The label for the pay rate.
 * @property {number} pay_rate - The amount of the pay rate.
 */
export interface IPayRateBase {
  id: ID;
  label: string;
  pay_rate: number;
}

/**
 * Interface representing a pay rate with associated employees.
 * @interface IPayRate
 * @extends {IPayRateBase}
 * @property {Array<{ contact_number: string; id: ID; name: string; status: EmployeeProfileStatus; }> | Array<ID>} employees - The list of employees associated with the pay rate.
 * @property {Array<ID>} [employee_ids] - Optional array of employee IDs.
 */
export interface IPayRate extends IPayRateBase {
  employees:
    | Array<{
        contact_number: string;
        id: ID;
        name: string;
        status: EmployeeProfileStatus;
      }>
    | Array<ID>;
  employee_ids?: Array<ID>;
}

/**
 * Interface representing an employee profile.
 * @interface IEmployeeProfile
 * @property {number} id - The unique identifier for the employee.
 * @property {string} name - The name of the employee.
 * @property {string} contact_number - The contact number of the employee.
 * @property {boolean|null} is_admin - Indicates if the employee is an admin.
 * @property {string} pin_code - The PIN code for the employee.
 * @property {Array<IRoleCompact>} roles - The list of roles assigned to the employee.
 * @property {number} status - The status of the employee.
 * @property {IPayRate} [pay_rate] - Optional pay rate information for the employee.
 */
export interface IEmployeeProfile {
  id: number;
  name: string;
  contact_number: string;
  is_admin: boolean | null;
  pin_code: string;
  roles: Array<IRoleCompact>;
  status: number;
  pay_rate?: IPayRate;
}

/**
 * Interface representing a paginated list of employee profiles.
 * @interface IEmployeeList
 * @extends {APIPaginatedResponse<IEmployeeProfile>}
 */
export interface IEmployeeList extends APIPaginatedResponse<IEmployeeProfile> {}

/**
 * Interface representing the employee service.
 * @interface IEmployeeService
 * @property {function(IListParams): UseQueryResult<IEmployeeList, Error>} list - Method to list employees based on the provided parameters.
 */
export interface IEmployeeService {
  list: (params: IListParams) => UseQueryResult<IEmployeeList, Error>;
  dataSourceListQuery: (
    params: IListParams,
  ) => Promise<AxiosResponse<IEmployeeList>>;
}

/**
 * Class representing the employee service implementation.
 * @class EmployeeService
 * @implements {IEmployeeService}
 */
export class EmployeeService implements IEmployeeService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  /**
   * Method to list employees based on the provided parameters.
   * @param {IListParams} params - The parameters for listing employees.
   * @returns {UseQueryResult<IEmployeeList, Error>} The result of the query.
   */
  list(params: IListParams): UseQueryResult<IEmployeeList, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["employees", "list"],
        venueID: null,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { isVenueSpecific = false, ...restParams } = updatedParams;
        const { data } = await this.api.get<AxiosResponse<IEmployeeList>>(
          "/personnel/employees/",
          {
            params: restParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
          },
        );

        return data;
      },
    });
  }

  /**
   * Method to directly query employees without using React Query.
   * @param {object} params - The parameters for querying employees.
   * @returns {Promise<AxiosResponse<IEmployeeList>>} The API response.
   */
  async dataSourceListQuery(
    params: IListParams,
  ): Promise<AxiosResponse<IEmployeeList>> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );

    return this.api.get<AxiosResponse<IEmployeeList>>("/personnel/employees/", {
      params: updatedParams,
      endpointService: EndpointService.Gateway,
    });
  }
}
